// src/contexts/UserContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { 
  isBeforeRamadan, 
  isWithinRamadan, 
  calculateRamadanDay as calculateDayFromDate,
  RAMADAN_REGIONS,
  DEFAULT_RAMADAN_REGION
} from '../utils/dateValidation';

export const UserContext = createContext();

const PRAYER_KEYS = ['fajr', 'zuhr', 'asr', 'maghrib', 'isha'];

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ramadanDay, setRamadanDay] = useState(0); // Initialize with 0 instead of calculating right away
  const [isHistoricalView, setIsHistoricalView] = useState(false);
  const [historicalDate, setHistoricalDate] = useState(null);
  const [currentViewData, setCurrentViewData] = useState(null);
  const [lastActiveDate, setLastActiveDate] = useState(null);

  // Helper function for consistent date formatting
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch user data function with useCallback
  const fetchUserData = useCallback(async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      
      const today = formatDate(new Date());
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // If user doesn't have a region set, try to detect it or set default
        if (!data.ramadanRegion) {
          try {
            // Try to detect region based on timezone
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            // Simple mapping of some common timezones to regions
            const regionMap = {
              // Expected Feb 18
              'America/': 'USA/Canada (Expected Feb 18)',
              'Europe/': 'Middle East/Europe (Expected Feb 18)',
              'Asia/Riyadh': 'Middle East/Europe (Expected Feb 18)',
              'Asia/Dubai': 'Middle East/Europe (Expected Feb 18)',
              
              // Possible Feb 19
              'Asia/Kolkata': 'South Asia (Possible Feb 19)',
              'Asia/Karachi': 'South Asia (Possible Feb 19)',
              'Asia/Dhaka': 'South Asia (Possible Feb 19)'
            };
            
            // Set region based on timezone prefix match
            let detectedRegion = DEFAULT_RAMADAN_REGION; // Default
            for (const [tzPrefix, region] of Object.entries(regionMap)) {
              if (timezone.startsWith(tzPrefix)) {
                detectedRegion = region;
                break;
              }
            }
            
            // Add region info to user data
            data.ramadanRegion = detectedRegion;
            data.ramadanStartDate = RAMADAN_REGIONS[detectedRegion];
            
            // Update Firestore with the detected region
            await updateDoc(userDocRef, {
              ramadanRegion: detectedRegion,
              ramadanStartDate: RAMADAN_REGIONS[detectedRegion]
            });
            
            console.log("Auto-detected region:", detectedRegion);
          } catch (error) {
            console.warn("Error detecting region:", error);
            // Set default if detection fails
            data.ramadanRegion = DEFAULT_RAMADAN_REGION;
            data.ramadanStartDate = RAMADAN_REGIONS[DEFAULT_RAMADAN_REGION];
          }
        }
        
        // Auto-correct legacy 2026 dates unless user has manually overridden
        const legacyDates = new Set(['2026-02-22', '2026-02-23', '2026-02-24']);
        const needsLegacyDefaultMigration =
          data.ramadanRegion === 'Likely start (Expected Feb 19)' &&
          data.ramadanStartDate === '2026-02-19';
        if (!data.ramadanStartDateOverride && (legacyDates.has(data.ramadanStartDate) || needsLegacyDefaultMigration)) {
          const correctedStart = RAMADAN_REGIONS[DEFAULT_RAMADAN_REGION];
          await updateDoc(userDocRef, {
            ramadanRegion: DEFAULT_RAMADAN_REGION,
            ramadanStartDate: correctedStart,
            ramadanLength: data.ramadanLength || 30
          });
          data.ramadanRegion = DEFAULT_RAMADAN_REGION;
          data.ramadanStartDate = correctedStart;
        }

        setUserData(data);
        setLastActiveDate(data.lastActiveDate || null);
        
        // Calculate Ramadan day with region awareness
        const currentRamadanDay = calculateDayFromDate(new Date(), data);
        setRamadanDay(currentRamadanDay);
      } else {
        // Initialize user data for first-time users
        
        // Try to auto-detect region
        let regionData = {
          ramadanRegion: DEFAULT_RAMADAN_REGION, // Default region
          ramadanStartDate: RAMADAN_REGIONS[DEFAULT_RAMADAN_REGION]
        };
        
        try {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const regionMap = {
            // Expected Feb 18
            'America/': 'USA/Canada (Expected Feb 18)',
            'Europe/': 'Middle East/Europe (Expected Feb 18)',
            'Asia/Riyadh': 'Middle East/Europe (Expected Feb 18)',
            'Asia/Dubai': 'Middle East/Europe (Expected Feb 18)',
            
            // Possible Feb 19
            'Asia/Kolkata': 'South Asia (Possible Feb 19)',
            'Asia/Karachi': 'South Asia (Possible Feb 19)',
            'Asia/Dhaka': 'South Asia (Possible Feb 19)'
          };
          
          for (const [tzPrefix, region] of Object.entries(regionMap)) {
            if (timezone.startsWith(tzPrefix)) {
              regionData.ramadanRegion = region;
              regionData.ramadanStartDate = RAMADAN_REGIONS[region];
              break;
            }
          }
          
          console.log("Auto-detected region for new user:", regionData.ramadanRegion);
        } catch (error) {
          console.warn("Error auto-detecting region for new user:", error);
          // Keep the default if detection fails
        }
        
        // Calculate Ramadan day with region awareness
        const currentRamadanDay = calculateDayFromDate(new Date(), regionData);
        
        // Create initial user data with region info
        const initialData = {
          day: currentRamadanDay,
          totalDays: 30,
          salah: { completed: 0, total: 5 },
          roza: false,
          taraweeh: false,
          quran: { completed: 0, total: 30 },
          namaz: {
            fajr: false,
            zuhr: false,
            asr: false,
            maghrib: false,
            isha: false
          },
          fasting: false,
          prayedTaraweeh: false,
          history: {},
          lastActiveDate: today,
          onboardingCompleted: false,
          ramadanLength: 30,
          ramadanLengthConfirmed: false,
          ramadanStartDateOverride: false,
          // Add region information
          ...regionData
        };
        
        await setDoc(userDocRef, initialData);
        setUserData(initialData);
        setLastActiveDate(today);
        setRamadanDay(currentRamadanDay);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed here

  // Auth state change listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("Auth state changed, user:", currentUser?.uid);
      setUser(currentUser);
      
      if (currentUser) {
        setLoading(true); // Set loading to true when user changes
        await fetchUserData(currentUser.uid);
      } else {
        // Clear all user data when logged out
        setUserData(null);
        setLastActiveDate(null);
        setRamadanDay(0);
        setIsHistoricalView(false);
        setHistoricalDate(null);
        setCurrentViewData(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [fetchUserData]);

  // Update user data function (memoized)
  const updateUserData = useCallback(async (newData) => {
    if (!user) return false;

    // Check if we're switching to or modifying historical view
    if (newData.hasOwnProperty('isHistoricalView')) {
      setIsHistoricalView(newData.isHistoricalView);
    }
    
    if (newData.hasOwnProperty('historicalDate')) {
      setHistoricalDate(newData.historicalDate);
    }
    
    if (newData.hasOwnProperty('currentViewData')) {
      setCurrentViewData(newData.currentViewData);
    }
    
    // If we're updating actual data (not just view state)
    const dataToUpdate = { ...newData };
    const historyDateOverride = dataToUpdate.historyDateOverride || null;
    
    // Remove view-only properties
    delete dataToUpdate.isHistoricalView;
    delete dataToUpdate.historicalDate;
    delete dataToUpdate.currentViewData;
    delete dataToUpdate.historyDateOverride;
    
    // If there's still data to update in Firestore
    if (Object.keys(dataToUpdate).length > 0) {
      try {
        if (historyDateOverride) {
          const userDocRef = doc(db, 'users', user.uid);
          const today = formatDate(new Date());
          const historyUpdate = {};

          for (const key in dataToUpdate) {
            historyUpdate[`history.${historyDateOverride}.${key}`] = dataToUpdate[key];
            if (historyDateOverride === today) {
              historyUpdate[key] = dataToUpdate[key];
            }
          }
          historyUpdate[`history.${historyDateOverride}.day`] = calculateDayFromDate(new Date(historyDateOverride), userData);

          await updateDoc(userDocRef, historyUpdate);

          setUserData(prevData => {
            if (!prevData) return prevData;
            const nextData = { ...prevData };
            if (!nextData.history) nextData.history = {};
            if (!nextData.history[historyDateOverride]) nextData.history[historyDateOverride] = {};

            for (const key in dataToUpdate) {
              nextData.history[historyDateOverride][key] = dataToUpdate[key];
              if (historyDateOverride === today) {
                nextData[key] = dataToUpdate[key];
              }
            }

            nextData.history[historyDateOverride].day = calculateDayFromDate(new Date(historyDateOverride), userData);
            return nextData;
          });

          return true;
        }

        // If we're in historical view and updating data, save to the history object
        if (isHistoricalView && historicalDate) {
          // Update the history object for the specific date
          const historyUpdate = {};
          
          // Structure the history update based on the data being updated
          for (const key in dataToUpdate) {
            historyUpdate[`history.${historicalDate}.${key}`] = dataToUpdate[key];
          }
          
          // Ensure we record what day of Ramadan this was
          historyUpdate[`history.${historicalDate}.day`] = calculateDayFromDate(new Date(historicalDate), userData);
          
          const userDocRef = doc(db, 'users', user.uid);
          await updateDoc(userDocRef, historyUpdate);
          
          // Update local state
          setUserData(prevData => {
            if (!prevData) return prevData; // Safety check
            
            const newData = { ...prevData };
            if (!newData.history) newData.history = {};
            if (!newData.history[historicalDate]) newData.history[historicalDate] = {};
            
            for (const key in dataToUpdate) {
              newData.history[historicalDate][key] = dataToUpdate[key];
            }
            
            newData.history[historicalDate].day = calculateDayFromDate(new Date(historicalDate), userData);
            
            return newData;
          });
        } else {
          // Normal update for today's data
          const today = formatDate(new Date()); // Use consistent date formatting
          const userDocRef = doc(db, 'users', user.uid);
          
          // Continue with normal update during Ramadan
          // Also record in history for today
          const historyUpdate = {};
          for (const key in dataToUpdate) {
            historyUpdate[`history.${today}.${key}`] = dataToUpdate[key];
          }
          historyUpdate[`history.${today}.day`] = ramadanDay;
          
          // Add lastActiveDate to regular updates
          if (!dataToUpdate.hasOwnProperty('lastActiveDate')) {
            dataToUpdate.lastActiveDate = today;
          }
          
          // Combine regular updates with history updates
          const combinedUpdates = { ...dataToUpdate, ...historyUpdate };
          
          await updateDoc(userDocRef, combinedUpdates);
          setUserData(prevData => {
            if (!prevData) return prevData; // Safety check
            const nextData = { ...prevData, ...dataToUpdate };
            if (!nextData.history) nextData.history = {};
            if (!nextData.history[today]) nextData.history[today] = {};
            for (const key in dataToUpdate) {
              nextData.history[today][key] = dataToUpdate[key];
            }
            nextData.history[today].day = ramadanDay;
            return nextData;
          });
          setLastActiveDate(today);
        }
        
        return true;
      } catch (error) {
        console.error("Error updating user data:", error);
        return false;
      }
    }
    
    // If we're just updating view state, update the local context data
    const nextIsHistoricalView = newData.hasOwnProperty('isHistoricalView')
      ? newData.isHistoricalView
      : isHistoricalView;
    const nextHistoricalDate = newData.hasOwnProperty('historicalDate')
      ? newData.historicalDate
      : historicalDate;
    const nextCurrentViewData = newData.hasOwnProperty('currentViewData')
      ? newData.currentViewData
      : currentViewData;

    setUserData(prevData => {
      if (!prevData) return prevData; // Safety check
      return {
        ...prevData,
        isHistoricalView: nextIsHistoricalView,
        historicalDate: nextHistoricalDate,
        currentViewData: nextCurrentViewData
      };
    });
    
    return true;
  }, [user, isHistoricalView, historicalDate, ramadanDay, userData, currentViewData]);

  // Check if the app needs to reset for a new day - using useCallback to avoid dependency issues
  const checkForDayChange = useCallback(async () => {
    if (!userData || !user) return;
    
    const today = formatDate(new Date());
    
    // Get the last active date from user data or use stored state
    const storedLastActiveDate = userData.lastActiveDate || lastActiveDate;
    
    // If this is our first check or the date has changed
    if (!storedLastActiveDate || storedLastActiveDate !== today) {
      console.log("New day detected! Resetting daily trackers.");
      
      // Calculate Ramadan day using region-aware function
      const currentRamadanDay = calculateDayFromDate(new Date(), userData);
      
      const todayHistory = userData?.history?.[today] || {};
      const seededNamaz = todayHistory.namaz || {
        fajr: false,
        zuhr: false,
        asr: false,
        maghrib: false,
        isha: false
      };
      const seededCompleted = todayHistory.salah?.completed
        ?? PRAYER_KEYS.filter((k) => seededNamaz?.[k]).length;

      // Preserve already-recorded today's history (e.g., Ramadan night mapped before midnight)
      const resetData = {
        namaz: seededNamaz,
        salah: {
          completed: seededCompleted,
          total: 5
        },
        fasting: todayHistory.fasting ?? false,
        prayedTaraweeh: (todayHistory.prayedTaraweeh ?? todayHistory.taraweeh) ?? false,
        lastActiveDate: today,
        day: currentRamadanDay
      };
      
      const userDocRef = doc(db, 'users', user.uid);
      const historyUpdate = {};
      for (const key in resetData) {
        historyUpdate[`history.${today}.${key}`] = resetData[key];
      }
      historyUpdate[`history.${today}.day`] = currentRamadanDay;
      const combinedUpdates = { ...resetData, ...historyUpdate };
      await updateDoc(userDocRef, combinedUpdates);
      setUserData(prevData => {
        if (!prevData) return prevData;
        const nextData = { ...prevData, ...resetData };
        if (!nextData.history) nextData.history = {};
        if (!nextData.history[today]) nextData.history[today] = {};
        for (const key in resetData) {
          nextData.history[today][key] = resetData[key];
        }
        nextData.history[today].day = currentRamadanDay;
        return nextData;
      });
      
      // Update local state
      setLastActiveDate(today);
      setRamadanDay(currentRamadanDay);
      
      // Reset any historical view or current view data
      setIsHistoricalView(false);
      setHistoricalDate(null);
      setCurrentViewData(null);
    }
  }, [userData, user, lastActiveDate]); // Add proper dependencies

  // Effect for checking day change - run on initial load and when userData changes
  useEffect(() => {
    if (userData && user) {
      checkForDayChange();
    }
  }, [userData, userData?.lastActiveDate, checkForDayChange, user]); // Add proper dependencies

  // Also check for day change periodically if the app is left open
  useEffect(() => {
    if (!user) return;
    
    // Check for day change every hour if the app remains open
    const dayChangeInterval = setInterval(() => {
      checkForDayChange();
    }, 3600000); // 1 hour in milliseconds
    
    return () => clearInterval(dayChangeInterval);
  }, [user, checkForDayChange]); // Add proper dependencies

  // Get the effective data to display (either current or historical)
  const getEffectiveData = useCallback(() => {
    if (!userData) return null; // Handle null userData case
    
    if (isHistoricalView && historicalDate && userData) {
      // If viewing historical data, combine base data with historical overrides
      const historyData = userData.history && userData.history[historicalDate] 
        ? userData.history[historicalDate] 
        : {};
      
      // Add flag for pre-Ramadan dates (with region awareness)
      const beforeRamadan = isBeforeRamadan(new Date(historicalDate), userData);
      
      return {
        ...userData,
        ...currentViewData,
        ...historyData,
        isHistoricalView,
        historicalDate,
        beforeRamadan // Add flag so components can check this
      };
    }
    
    // Otherwise return current data with beforeRamadan flag if needed (with region awareness)
    return {
      ...userData,
      beforeRamadan: isBeforeRamadan(new Date(), userData)
    };
  }, [userData, isHistoricalView, historicalDate, currentViewData]);

  // Track daily actions in history
  const recordDailyAction = useCallback(async (action, value) => {
    if (!user || !userData) return false;

    const userDocRef = doc(db, 'users', user.uid);
    const isHistorical = isHistoricalView && historicalDate;
    const dateKey = isHistorical ? historicalDate : formatDate(new Date());
    const dayValue = isHistorical
      ? calculateDayFromDate(new Date(historicalDate), userData)
      : ramadanDay;

    const historyUpdate = {
      [`history.${dateKey}.${action}`]: value,
      [`history.${dateKey}.day`]: dayValue
    };

    try {
      await updateDoc(userDocRef, historyUpdate);
      setUserData(prevData => {
        if (!prevData) return prevData;
        const next = { ...prevData };
        if (!next.history) next.history = {};
        if (!next.history[dateKey]) next.history[dateKey] = {};
        next.history[dateKey][action] = value;
        next.history[dateKey].day = dayValue;
        return next;
      });
      return true;
    } catch (error) {
      console.error('Error recording daily action:', error);
      return false;
    }
  }, [user, userData, isHistoricalView, historicalDate, ramadanDay]);

  // Use the memoized getEffectiveData to compute the context value
  const effectiveData = userData ? getEffectiveData() : null;

  const value = {
    user,
    userData: effectiveData,
    loading,
    ramadanDay,
    isHistoricalView,
    historicalDate,
    updateUserData,
    recordDailyAction,
    checkForDayChange, // Expose this method to allow manual refresh
    isBeforeRamadan: (date) => isBeforeRamadan(date, userData), // Wrap to pass userData
    isWithinRamadan: (date) => isWithinRamadan(date, userData)  // Wrap to pass userData
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
